import { Pause, Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const learningDeskImage = "/images/learning-desk.webp";

const storySteps = [
  {
    id: "all",
    label: "The complete learning experience",
    title: "Learning built around the student",
    description:
      "Curriculum alignment, a dedicated tutor, focused practice, and useful feedback work together as one connected experience.",
    clipPath: "inset(0% 0% 0% 0% round 16px)",
  },
  {
    id: "curriculum",
    label: "Curriculum alignment",
    title: "Start with what the student needs to learn",
    description:
      "Grade, curriculum, subject, and learning objectives give each tutoring plan a clear academic foundation.",
    clipPath: "inset(43% 64% 22% 12% round 16px)",
  },
  {
    id: "tutor",
    label: "Live tutor guidance",
    title: "Work through challenges with a real tutor",
    description:
      "The tutor explains, questions, and adapts the lesson while the student actively works through the problem.",
    clipPath: "inset(7% 7% 53% 66% round 16px)",
  },
  {
    id: "practice",
    label: "Focused practice",
    title: "Turn explanation into understanding",
    description:
      "The student applies the idea immediately, making each session active, specific, and connected to the objective.",
    clipPath: "inset(43% 18% 24% 59% round 16px)",
  },
  {
    id: "feedback",
    label: "Meaningful feedback",
    title: "Know what is understood and what comes next",
    description:
      "Clear feedback helps the student recognise progress and gives the tutor a better starting point for the next lesson.",
    clipPath: "inset(55% 5% 18% 78% round 16px)",
  },
];

const STEP_DURATION = 5000;

const FeatureSection = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotionPreference = () => {
      setPrefersReducedMotion(mediaQuery.matches);
      if (mediaQuery.matches) setIsPlaying(false);
    };

    updateMotionPreference();
    mediaQuery.addEventListener("change", updateMotionPreference);
    return () => mediaQuery.removeEventListener("change", updateMotionPreference);
  }, []);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.35 },
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isPlaying || !isVisible || prefersReducedMotion) return;

    const interval = window.setInterval(() => {
      setActiveStep((current) => (current + 1) % storySteps.length);
    }, STEP_DURATION);

    return () => window.clearInterval(interval);
  }, [isPlaying, isVisible, prefersReducedMotion]);

  const step = storySteps[activeStep];
  const isOverview = step.id === "all";

  const selectStep = (index: number) => {
    setActiveStep(index);
    setIsPlaying(false);
  };

  return (
    <section ref={sectionRef} className="overflow-hidden bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-10 max-w-3xl text-center sm:mb-12">
          <h2 className="text-3xl font-extrabold tracking-[-0.025em] text-gray-950 sm:text-4xl lg:text-5xl">
            Every lesson starts with where the student is
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg leading-8 text-gray-600">
            See how curriculum-aligned tutoring connects the right objective,
            human guidance, active practice, and feedback.
          </p>
        </div>

        <div
          className="relative isolate mx-auto max-w-6xl overflow-hidden rounded-2xl bg-[#f2e9ff] shadow-[0_24px_70px_-32px_rgba(62,17,104,0.45)]"
          style={{ height: "clamp(560px, 56.25vw, 648px)" }}
        >
          <img
            src={learningDeskImage}
            alt="An older student solving a mathematics problem with a tutor during an online lesson"
            width={1672}
            height={941}
            loading="eager"
            fetchPriority="high"
            className="absolute inset-0 h-full w-full object-cover transition-[filter,transform] duration-700 ease-out"
            style={{
              filter: isOverview
                ? "blur(0px) brightness(1)"
                : "blur(7px) brightness(0.62)",
              transform: isOverview ? "scale(1)" : "scale(1.012)",
            }}
          />

          {!isOverview && (
            <img
              key={step.id}
              src={learningDeskImage}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover opacity-100 transition-[clip-path,opacity] duration-700 ease-out motion-reduce:transition-opacity"
              style={{ clipPath: step.clipPath, zIndex: 10 }}
            />
          )}

          <div
            className="pointer-events-none absolute inset-0"
            style={{
              zIndex: 5,
              background: isOverview
                ? "radial-gradient(ellipse 58% 48% at 20% 20%, rgba(255,255,255,0.99) 0%, rgba(255,255,255,0.9) 48%, rgba(255,255,255,0.3) 76%, rgba(255,255,255,0) 100%)"
                : "linear-gradient(90deg, rgba(255,255,255,0.99) 0%, rgba(255,255,255,0.94) 31%, rgba(255,255,255,0.58) 49%, rgba(255,255,255,0.12) 67%, rgba(255,255,255,0) 78%)",
            }}
          />

          <div
            className="absolute"
            style={{
              top: "clamp(24px, 4vw, 48px)",
              left: "clamp(24px, 4vw, 48px)",
              width: "min(420px, calc(100% - 48px))",
              zIndex: 20,
            }}
            aria-live="polite"
          >
            {storySteps.map((storyStep, index) => (
              <div
                key={storyStep.id}
                aria-hidden={index !== activeStep}
                className={`p-2 transition-[opacity,transform] duration-700 ease-out sm:p-3 ${
                  index === activeStep
                    ? "relative translate-y-0 opacity-100"
                    : "pointer-events-none absolute inset-0 translate-y-2 opacity-0"
                }`}
                style={{
                  fontFamily: "Nunito, sans-serif",
                }}
              >
                <p
                  className="mb-3 text-sm font-extrabold uppercase tracking-wide"
                  style={{ color: "#6e59a5" }}
                >
                  {storyStep.label}
                </p>
                <h3
                  className="text-xl font-extrabold leading-tight tracking-tight sm:text-2xl md:text-3xl"
                  style={{
                    maxWidth: "20ch",
                    color: "#6e59a5",
                  }}
                >
                  {storyStep.title}
                </h3>
                <p
                  className="mt-5 text-base font-normal"
                  style={{ maxWidth: "40ch", color: "hsl(240 10% 3.9%)", lineHeight: 1.6 }}
                >
                  {storyStep.description}
                </p>
              </div>
            ))}
          </div>

          <div
            className="absolute flex items-center justify-between gap-3"
            style={{
              left: "clamp(24px, 3vw, 32px)",
              right: "clamp(24px, 3vw, 32px)",
              bottom: "clamp(20px, 3vw, 28px)",
              zIndex: 30,
            }}
          >
            <div
              className="flex items-center gap-2 rounded-full bg-[#fffaf7]/92 px-3 py-2 shadow-sm backdrop-blur-sm"
              aria-label="Learning story steps"
            >
              {storySteps.map((storyStep, index) => (
                <button
                  key={storyStep.id}
                  type="button"
                  onClick={() => selectStep(index)}
                  className={`h-2.5 rounded-full transition-[width,background-color] duration-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6e3bb5] focus-visible:ring-offset-2 ${
                    index === activeStep
                      ? "w-8 bg-[#ff7f52]"
                      : "w-2.5 bg-[#9d85c7] hover:bg-[#6e3bb5]"
                  }`}
                  aria-label={`Show ${storyStep.label}`}
                  aria-current={index === activeStep ? "step" : undefined}
                />
              ))}
            </div>

            {!prefersReducedMotion && (
              <button
                type="button"
                onClick={() => setIsPlaying((current) => !current)}
                className="flex min-h-11 items-center gap-2 rounded-full bg-[#fffaf7]/95 px-4 py-2 text-sm font-semibold text-[#3e1168] shadow-sm backdrop-blur-sm transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6e3bb5] focus-visible:ring-offset-2"
                aria-label={isPlaying ? "Pause learning story" : "Play learning story"}
              >
                {isPlaying ? (
                  <Pause aria-hidden="true" size={16} />
                ) : (
                  <Play aria-hidden="true" size={16} />
                )}
                <span className="hidden sm:inline">
                  {isPlaying ? "Pause" : "Play"}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureSection;
